// #189 Rut Tanımlama "Müşteriler" sekmesi için TASLAK (işlemsel) durum yönetimi.
//
// Önceki davranış: havuza ekleme/çıkarma, frekans/gün değişimi ve toplu işlemler
// backend'e ANINDA yazılıyordu; üstteki "Vazgeç" yalnız Genel formu geri aldığı için
// kullanıcı "kaydetmediğim halde kaydolmuş" diyordu (#189).
//
// Yeni davranış: tüm müşteri değişiklikleri istemcide tamponlanır (working rows).
//   • flush()   → üstteki "Değişiklikleri Kaydet"te tek seferde backend'e yazar.
//   • discard() → "Vazgeç"te sunucu kopyasına döner (bekleyenleri atar).
// Hook PARENT'ta (RoutePlanDetailPage) tutulur ki sekme değiştirince (CustomersTab
// unmount olunca) bekleyen değişiklikler kaybolmasın.
import { useState, useCallback, useRef, useMemo } from 'react';
import { authHeaders } from '../listPageStyles';
import { API_BASE, pick, unwrap, FREQUENCY_OPTIONS } from './routePlanUtils';

// Server satırından çoklu-gün dizisi (1..7), tekilleştirilmiş + sıralı.
function serverRowDays(r) {
  const arr = pick(r, 'dayNos', 'DayNos');
  if (Array.isArray(arr) && arr.length) return [...new Set(arr.map(Number))].sort((a, b) => a - b);
  const d = pick(r, 'dayNo', 'DayNo');
  return d ? [Number(d)] : [];
}

const sameDays = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

// Server satırını dahili "çalışma satırı"na normalize et. _base* alanları diff (bekleyen
// değişiklik tespiti) için orijinal sunucu değerini saklar; _key seçim/identite anahtarıdır.
function normalizeServerRow(r) {
  const days = serverRowDays(r);
  return {
    _origin: 'server',
    _key: `s:${pick(r, 'id', 'Id')}`,
    _removed: false,
    id: pick(r, 'id', 'Id'),
    customerId: pick(r, 'customerId', 'CustomerId'),
    customerTextId: pick(r, 'customerTextId', 'CustomerTextId') ?? '',
    customerTitle: pick(r, 'customerTitle', 'CustomerTitle') ?? '',
    address: pick(r, 'address', 'Address') ?? '',
    region: pick(r, 'region', 'Region') ?? '',
    province: pick(r, 'province', 'Province') ?? '',
    neighborhood: pick(r, 'neighborhood', 'Neighborhood') ?? '',
    city: pick(r, 'city', 'City') ?? '',
    latitude: pick(r, 'latitude', 'Latitude'),
    longitude: pick(r, 'longitude', 'Longitude'),
    visitOrderNo: pick(r, 'visitOrderNo', 'VisitOrderNo') ?? null,
    // #805 Müşterinin bağlı olduğu distribütör — planın kapsamıyla karşılaştırılır.
    vendorId: pick(r, 'vendorId', 'VendorId') ?? null,
    vendorName: pick(r, 'vendorName', 'VendorName') ?? null,
    frequency: pick(r, 'frequency', 'Frequency') ?? null,
    dayNos: days,
    _baseFrequency: pick(r, 'frequency', 'Frequency') ?? null,
    _baseDayNos: days,
  };
}

// Bir çalışma satırının sunucu değerine göre programı değişmiş mi?
function isRowChanged(r) {
  return (r.frequency ?? null) !== (r._baseFrequency ?? null) || !sameDays(r.dayNos, r._baseDayNos);
}

export function useRoutePlanCustomers(planId, showToast) {
  const [rows, setRows] = useState([]); // çalışma satırları (server + new, _removed bayraklı)
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const tempIdRef = useRef(0);

  // Sunucudan taze yükle → çalışma kopyasını sıfırla (bekleyenleri temizler).
  const reload = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${planId}/customers`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Müşteriler alınamadı (${res.status})`);
      const list = unwrap(await res.json()) || [];
      setRows(list.map(normalizeServerRow));
      setLoaded(true);
    } catch (e) {
      showToast?.('error', e.message);
    } finally { setLoading(false); }
  }, [planId, showToast]);

  // "Vazgeç": yeni eklenenleri at, silinenleri geri al, program değişikliklerini sunucu
  // değerine döndür. Yeniden fetch'e gerek yok; _base* değerleri elde tutulur.
  const discard = useCallback(() => {
    setRows((prev) => prev
      .filter((r) => r._origin === 'server')
      .map((r) => ({ ...r, _removed: false, frequency: r._baseFrequency, dayNos: [...r._baseDayNos] })));
  }, []);

  // Havuza müşteri ekle (taslak). poolRows = AddCustomersModal pool-search satırları.
  // Zaten aktif olan müşteri atlanır; daha önce "çıkar" denmiş bir müşteri geri alınır.
  const addCustomers = useCallback((poolRows, frequency, days, fallbackCity) => {
    const dayList = [...new Set((days || []).map(Number))].sort((a, b) => a - b);
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      const byCustomer = new Map(next.map((r) => [String(r.customerId), r]));
      (poolRows || []).forEach((pr) => {
        const cid = pick(pr, 'id', 'Id');
        if (cid == null) return;
        const existing = byCustomer.get(String(cid));
        if (existing) {
          // Çıkarılmış sunucu satırıysa geri al + yeni programı uygula; aktifse dokunma.
          if (existing._removed) {
            existing._removed = false;
            existing.frequency = frequency ?? null;
            existing.dayNos = [...dayList];
          }
          return;
        }
        tempIdRef.current += 1;
        const row = {
          _origin: 'new',
          _key: `n:${tempIdRef.current}`,
          _removed: false,
          _tempId: tempIdRef.current,
          id: null,
          customerId: cid,
          customerTextId: pick(pr, 'textId', 'TextId') ?? '',
          customerTitle: pick(pr, 'title', 'Title') ?? '',
          address: pick(pr, 'address', 'Address') ?? '',
          region: pick(pr, 'region', 'Region') ?? '',
          province: pick(pr, 'province', 'Province') ?? '',
          neighborhood: pick(pr, 'neighborhood', 'Neighborhood') ?? '',
          city: pick(pr, 'city', 'City') ?? fallbackCity ?? '',
          latitude: pick(pr, 'latitude', 'Latitude'),
          longitude: pick(pr, 'longitude', 'Longitude'),
          // #805 Havuz satırı da distribütörünü taşır; henüz kaydedilmemiş satırlar da
          // kapsam tutarlılığı kontrolüne girsin.
          vendorId: pick(pr, 'vendorId', 'VendorId') ?? null,
          vendorName: pick(pr, 'vendorName', 'VendorName') ?? null,
          visitOrderNo: null,
          frequency: frequency ?? null,
          dayNos: [...dayList],
        };
        next.push(row);
        byCustomer.set(String(cid), row);
      });
      return next;
    });
  }, []);

  // Satırı çıkar (taslak): yeni satır tamamen düşer, sunucu satırı _removed işaretlenir.
  const removeRow = useCallback((key) => {
    setRows((prev) => prev.flatMap((r) => {
      if (r._key !== key) return [r];
      if (r._origin === 'new') return [];
      return [{ ...r, _removed: true }];
    }));
  }, []);

  // #808 Toplu çıkarma — removeRow'un küme hâli. Kapsam dışı müşterileri tek düğmeyle
  // temizlemek için gerekli: 60 satırı tek tek çıkarmak hem yavaş hem her çağrıda
  // tüm listeyi yeniden üretiyordu. Semantik removeRow ile aynıdır (taslak; "Değişiklikleri
  // Kaydet"e kadar backend'e yazılmaz).
  const removeRows = useCallback((keys) => {
    const keySet = new Set(keys || []);
    if (keySet.size === 0) return;
    setRows((prev) => prev.flatMap((r) => {
      if (!keySet.has(r._key)) return [r];
      if (r._origin === 'new') return [];
      return [{ ...r, _removed: true }];
    }));
  }, []);

  // Tek satır program değişimi (satır içi frekans/gün select'leri).
  const setSchedule = useCallback((key, patch) => {
    setRows((prev) => prev.map((r) => {
      if (r._key !== key) return r;
      const nextRow = { ...r };
      if (patch.frequency !== undefined) nextRow.frequency = patch.frequency || null;
      if (patch.dayNos !== undefined) nextRow.dayNos = [...new Set(patch.dayNos.map(Number))].sort((a, b) => a - b);
      return nextRow;
    }));
  }, []);

  // Toplu yeniden programlama (Ata/Kaydır/Çıkar) — backend bulk-reschedule semantiğinin
  // istemci karşılığı: additive=true mevcut günleri korur+ekler, false değiştirir/çıkarır.
  const bulkReschedule = useCallback(({ keys, fromDayNo = null, toDayNos = [], frequency = null, additive = false }) => {
    const keySet = new Set(keys);
    const tos = [...new Set(toDayNos.map(Number))].sort((a, b) => a - b);
    setRows((prev) => prev.map((r) => {
      if (!keySet.has(r._key) || r._removed) return r;
      if (fromDayNo && !r.dayNos.includes(fromDayNo)) return r;
      let nextDays;
      if (tos.length === 0) nextDays = [];
      else if (additive) nextDays = [...new Set([...r.dayNos, ...tos])].sort((a, b) => a - b);
      else nextDays = [...tos];
      return { ...r, dayNos: nextDays, frequency: frequency ?? r.frequency };
    }));
  }, []);

  // Bekleyen değişiklik özeti.
  const pending = useMemo(() => {
    let adds = 0; let removes = 0; let changes = 0;
    rows.forEach((r) => {
      if (r._origin === 'new') { adds += 1; return; }
      if (r._removed) { removes += 1; return; }
      if (isRowChanged(r)) changes += 1;
    });
    return { adds, removes, changes, total: adds + removes + changes };
  }, [rows]);

  const activeCount = useMemo(() => rows.filter((r) => !r._removed).length, [rows]);

  // Bekleyen değişiklikleri tek seferde backend'e yaz. Sıra: çıkar → program değiştir → ekle.
  // Müşteri-id ile çalışıldığı için yeni satır rpcId bağımlılığı oluşmaz (çoklu-gün ekleme
  // AddCustomersModal'daki 2 adımlı deseni grup başına tekrarlar).
  const flush = useCallback(async () => {
    const snapshot = rows;

    // 1) Çıkarılanlar (sunucu satırları).
    const removeIds = snapshot.filter((r) => r._origin === 'server' && r._removed).map((r) => r.id);
    for (const rpcId of removeIds) {
      const res = await fetch(`${API_BASE}/${planId}/customers/${rpcId}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Müşteri çıkarma başarısız');
    }

    // 2) Program değişen sunucu satırları — (frekans, günler) grubuna göre bulk-reschedule.
    const changed = snapshot.filter((r) => r._origin === 'server' && !r._removed && isRowChanged(r));
    const changeGroups = new Map();
    changed.forEach((r) => {
      const k = `${r.frequency ?? ''}|${r.dayNos.join(',')}`;
      if (!changeGroups.has(k)) changeGroups.set(k, { frequency: r.frequency ?? null, dayNos: r.dayNos, ids: [] });
      changeGroups.get(k).ids.push(r.id);
    });
    for (const g of changeGroups.values()) {
      const res = await fetch(`${API_BASE}/${planId}/customers/bulk-reschedule`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ routePlanCustomerIds: g.ids, fromDayNo: null, toDayNos: g.dayNos, frequency: g.frequency, additive: false }),
      });
      if (!res.ok) { const p = await res.json().catch(() => null); throw new Error(pick(p, 'Message', 'message') || 'Program güncelleme başarısız'); }
    }

    // 3) Yeni eklenenler — (frekans, günler) grubuna göre POST; çoklu-gün için intervals.
    const adds = snapshot.filter((r) => r._origin === 'new');
    const addGroups = new Map();
    adds.forEach((r) => {
      const k = `${r.frequency ?? ''}|${r.dayNos.join(',')}`;
      if (!addGroups.has(k)) addGroups.set(k, { frequency: r.frequency ?? null, dayNos: r.dayNos, customerIds: [] });
      addGroups.get(k).customerIds.push(r.customerId);
    });
    for (const g of addGroups.values()) {
      const days = g.dayNos;
      const body = { customerIds: g.customerIds, frequency: g.frequency, dayNo: days.length === 1 ? days[0] : null };
      const res = await fetch(`${API_BASE}/${planId}/customers`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const p = await res.json().catch(() => null); throw new Error(pick(p, 'Message', 'message') || 'Ekleme başarısız'); }
      if (days.length > 1) {
        const listRes = await fetch(`${API_BASE}/${planId}/customers`, { headers: authHeaders() });
        const poolRows = listRes.ok ? (unwrap(await listRes.json()) || []) : [];
        const wantSet = new Set(g.customerIds.map(String));
        const rpcIds = poolRows
          .filter((row) => wantSet.has(String(pick(row, 'customerId', 'CustomerId'))))
          .map((row) => pick(row, 'id', 'Id'))
          .filter(Boolean);
        if (rpcIds.length > 0) {
          const weeks = FREQUENCY_OPTIONS.find((f) => f.value === g.frequency)?.weeks || [1, 2, 3, 4];
          const ir = await fetch(`${API_BASE}/${planId}/intervals`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ routePlanCustomerIds: rpcIds, weeks, days, intervalDesc: g.frequency || null }),
          });
          if (!ir.ok) { const ip = await ir.json().catch(() => null); throw new Error(pick(ip, 'Message', 'message') || 'Gün ataması başarısız'); }
        }
      }
    }

    await reload();
  }, [rows, planId, reload]);

  return {
    rows, loading, loaded, pending, activeCount,
    reload, discard, flush,
    addCustomers, removeRow, removeRows, setSchedule, bulkReschedule,
  };
}

export default useRoutePlanCustomers;
